import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { posthog } from '../main.jsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resolveVerifyRedirect } from '../lib/navigation/resolvePostLoginRedirect'; // #1521/#1562/#1857
import { toUserFacingMessage } from '../utils/userFacingError.js';
import {
  getMagicLinkParam,
  markMagicLinkConsumed,
} from '../lib/auth/magicLinkUrlScrub.js'; // #3173, #3262

function VerifyPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  // #3738: `error` is null, `{ key }` (an i18n key, translated at render so the
  // line re-translates once the lazily loaded resource arrives; storing t()'s
  // result froze the raw key into state), or `{ text }` (an already-built
  // user-facing string).
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const verifyAttempted = useRef(false);

  const { verifyMagicLink, loginWithGoogle } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Issue #1155: track that the user clicked an email link, plus reminder-stage attribution.
  // ?ref=reminder1|reminder2 is appended by the day-3/day-5 reminder cron (Phase 1 backend).
  useEffect(() => {
    const ref = searchParams.get('ref');
    posthog?.capture?.('auth_email_link_clicked', {
      surface: 'verify_page',
      ref: ref || null,
    });
    if (ref === 'reminder1' || ref === 'reminder2') {
      posthog?.capture?.('auth_reminder_email_clicked', { stage: ref });
    }
    // mount-only
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Prevent multiple verify attempts (React StrictMode, re-renders, etc.)
    if (verifyAttempted.current) {
      return;
    }
    verifyAttempted.current = true;

    const verifyToken = async () => {
      // #3173: main.jsx's pre-init scrub moved token/email out of the URL;
      // getMagicLinkParam reads them back from where it stashed them.
      const token = getMagicLinkParam(searchParams, 'token');
      const email = getMagicLinkParam(searchParams, 'email');

      if (!token || !email) {
        setError({ key: 'verify.invalidLink' });
        setIsLoading(false);
        return;
      }

      try {
        const response = await verifyMagicLink(token, decodeURIComponent(email));
        markMagicLinkConsumed(); // #3173: a stashed link is single-use
        setSuccess(true);
        setIsNewUser(response.is_new_user || false);

        // Issue #1893 loop closure: capture→upgrade conversion previously had
        // no reader — nothing measured whether a captured email ever turned
        // into a real account. `is_guest_upgrade` comes straight off the
        // /auth/verify response (routes/auth.js), not inferred client-side.
        // Read by loop-closure-weekly task 14's guest-funnel query.
        posthog?.capture?.('auth_verify_succeeded', {
          is_new_user: response.is_new_user || false,
          is_guest_upgrade: response.is_guest_upgrade || false,
        });

        // Redirect to stored destination or the flag-resolved learner home
        // (Issue #335: shareable links; #1521: resolved from the verify
        // RESPONSE branding — context values are stale in this closure;
        // #1562: a stored home alias is residue, only deep links win;
        // #1857: the magic link's own `redirect` query param — set by the
        // "Keep this plan" capture-email flow — wins over sessionStorage
        // since it survives cross-tab/cross-device opens).
        const redirectTo = resolveVerifyRedirect(
          searchParams.get('redirect'),
          sessionStorage.getItem('loginRedirect'),
          response.user?.branding,
        );
        sessionStorage.removeItem('loginRedirect');

        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 2000);

      } catch (err) {
        setError({ text: toUserFacingMessage(err, t, 'Failed to verify link') });
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is used only for error copy; the magic-link verification must not re-run on language change. Keyed on searchParams/verifyMagicLink/navigate
  }, [searchParams, verifyMagicLink, navigate]);

  // #3262 item 1 P1 review-fix (PR #3271): a prior review-fix released the
  // deferred LinkedIn/Meta vendor scripts (releaseDeferredThirdPartyTags) on
  // every exit from this failure view, to recover ad-conversion tracking for
  // a sign-in later in this document. That is unsafe: a verify failure does
  // NOT prove the stashed token in window.__tacitMagicLinkStash (frozen,
  // still readable) is dead. AuthService.verifyMagicLinkToken's one-time-use
  // check is an atomic Redis GETDEL — if the failure was a client-side
  // network error (fetch never reached the backend) or the backend's own
  // "Authentication service temporarily unavailable" Redis-outage path
  // (backend/src/services/auth.js), GETDEL never ran and the bearer token is
  // still fully redeemable. Releasing here would load vendor script (full
  // window access, not CSP-hash-pinned like our own inline scripts) into the
  // same document while that live secret sits in a page global, letting a
  // compromised/malicious vendor script read it and replay it against
  // /auth/verify for account takeover. Deferred tags are released ONLY by
  // markMagicLinkConsumed(), which fires solely on a successful verify (the
  // one case the Redis GETDEL is guaranteed to have already consumed the
  // token). Do not add a release call on any failure-view exit path without
  // first getting a positive, server-verified "this token is now dead"
  // signal.
  const handleRequestNewLink = () => {
    posthog?.capture?.('auth_request_new_link_clicked', { surface: 'verify_failure' });
    const email = getMagicLinkParam(searchParams, 'email');
    const target = email
      ? `/login?email=${encodeURIComponent(decodeURIComponent(email))}`
      : '/login';
    navigate(target);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    posthog?.capture?.('auth_provider_selected', {
      provider: 'google',
      surface: 'verify_failure',
    });
    try {
      const response = await loginWithGoogle(credentialResponse.credential);
      if (response.success) {
        const expectedEmailRaw = getMagicLinkParam(searchParams, 'email');
        const expectedEmail = expectedEmailRaw
          ? decodeURIComponent(expectedEmailRaw).toLowerCase()
          : null;
        const actualEmail = response.user?.email?.toLowerCase();
        if (expectedEmail && actualEmail && expectedEmail !== actualEmail) {
          showToast(
            t('auth.googleMismatchToast', {
              googleEmail: response.user.email,
              inviteEmail: decodeURIComponent(expectedEmailRaw),
            }),
            'info',
            10000,
          );
        }
        const redirectTo = resolveVerifyRedirect(
          searchParams.get('redirect'),
          sessionStorage.getItem('loginRedirect'),
          response.user?.branding,
        ); // #1521/#1562/#1857
        sessionStorage.removeItem('loginRedirect');
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError({ text: toUserFacingMessage(err, t, 'Failed to sign in with Google') });
    } finally {
      setGoogleLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="verify-page">
        <div className="verify-container">
          <div className="loading-spinner" />
          <h2>{t('verify.verifying')}</h2>
          <p>{t('verify.pleaseWait')}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="verify-page">
        <div className="verify-container">
          <div className="success-icon">{isNewUser ? '🎉' : '✅'}</div>
          <h2>{isNewUser ? t('verify.welcomeNew') : t('verify.welcomeBack')}</h2>
          <p>
            {isNewUser
              ? t('verify.accountReady')
              : t('verify.redirecting')
            }
          </p>
        </div>
      </div>
    );
  }

  const googleEnabled = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
  // #3262 item 4 (D-178 b): the Google button gets no email hint here. A hint
  // sends the magic-link email to accounts.google.com, on the one page whose
  // job is to keep it private. The local uses in handleGoogleSuccess (mismatch
  // toast) and handleRequestNewLink (/login prefill) stay.

  return (
    <div className="verify-page">
      <div className="verify-container">
        <div className="error-icon">❌</div>
        <h2>{t('verify.failed')}</h2>
        <p className="verify-failed-subtitle">{t('verify.failedSubtitle')}</p>
        {error && <p className="error-message">{error.key ? t(error.key) : error.text}</p>}

        {googleEnabled && (
          <div className="verify-google-section">
            <p className="auth-section-helper">{t('verify.googleHelperText')}</p>
            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError({ text: t('login.googleSignInFailed') })}
                theme="outline"
                size="large"
                text="signin_with"
                width="100%"
              />
            </div>
            {googleLoading && <p>{t('verify.verifying')}</p>}
          </div>
        )}

        <div className="verify-actions">
          <button
            onClick={handleRequestNewLink}
            className="retry-button"
          >
            {t('verify.requestNewLink')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="home-button"
          >
            {t('verify.goHome')}
          </button>
        </div>

        <div className="verify-help">
          <h3>{t('verify.commonIssues')}</h3>
          <ul>
            <li>{t('verify.issueExpired')}</li>
            <li>{t('verify.issueUsedOnce')}</li>
            <li>{t('verify.issueCompleteLink')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default VerifyPage;
